import React from 'react'
import { Formik, FieldArray } from 'formik'
import Select from 'react-select'
import * as Yup from 'yup'
import { t } from '../../i18n'
import { MoveMode, MoveModeType, DurationType } from './models'

interface MoveModeFormProps {
  moveMode?: MoveMode
  cookingMoveSteps?: any[]
  unloadMoveSteps?: any[]
  organizationOptions?: any[]
  onSubmitForm: (values: any, callback: (success: boolean, errorData: any) => void) => void
}

const MoveModeForm: React.FC<MoveModeFormProps> = ({
  moveMode = {
    id: null,
    name: '',
    description: '',
    move_mode_type: 'cooking_mode' as MoveModeType,
    move_mode_organizations: [],
    move_mode_steps: Array(1).fill({
      id: null,
      duration: 0,
      duration_type: 'seconds' as DurationType,
      step_number: 1,
      servo_move_step_id: null }),
  },
  cookingMoveSteps = [],
  unloadMoveSteps = [],
  organizationOptions = [],
  onSubmitForm,
}) => {

  const validationSchema = Yup.object({
    name: Yup.string().required(t('move_modes.validations.name')),
    move_mode_steps: Yup.array()
      .of(
        Yup.object({
          servo_move_step_id: Yup.number().required(t('move_modes.validations.submove')),
          duration: Yup.number().required(t('move_modes.validations.duration')),
          duration_type: Yup.string().required(t('move_modes.validations.duration_type')),
        })
      )
  })

  return (
    <Formik
      initialValues={moveMode}
      validationSchema={validationSchema}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        const payload = { ...values }
        return onSubmitForm(payload, (success, errorData) => {
          let status = {}
          if (!success) {
            const fields = Object.getOwnPropertyNames(errorData.full_errors)
            for (let key in fields) {
              status[`${fields[key]}_error`] =
                errorData.full_errors[fields[key]].join(', ')
            }
          }
          setStatus(status)
          setSubmitting(false)
        })
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setStatus,
      }) => {
        const enhancedHandleChange = (e) => {
          let newStatus = Object.assign({}, status)
          newStatus[`${e.target.name}_error`] = ''
          setStatus(newStatus)
          handleChange(e)
        }

        const renderField = (label, name, type, placeholder, value=null, inverted = true, autoComplete = 'off') => {
          const inputValue = value !== null ? value : values[name]
          const errorsPresent =
            (errors[name] && touched[name]) || (status && status[`${name}_error`])
          return (
            <div className="form-group">
              <label>{label}</label>
              <input
                id={name}
                name={name}
                type={type}
                onBlur={handleBlur}
                value={inputValue === null ? '' : inputValue}
                placeholder={placeholder}
                autoComplete={autoComplete}
                onChange={enhancedHandleChange}
                className={`form-control ${errorsPresent && 'is-invalid'} ${inverted && 'inverted'}`}
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {errors[name] || (status && status[`${name}_error`])}
                </div>
              )}
            </div>
          )
        }

        const renderMoveModeTypeRadio = () => {
          return (
            <div style={{
              padding: '10px',
              backgroundColor: 'white',
              borderRadius: '10px',
              marginTop: '10px',
              marginBottom: '20px',
            }}>
              <label style={{
                fontSize: '12px',
                color: '#6F6F6F',
                marginLeft: '5px',
                marginBottom: '10px'
                }}
              >
                {t('move_modes.move_mode_type')}
              </label>
              <div>
                {['cooking_mode', 'unload_mode'].map((option, index) => (
                  <div key={index} style={{display: 'block', padding: '7px'}}>
                    <input
                      style={{marginBottom: '2px'}}
                      type="radio"
                      name="move_mode_type"
                      id={`move_mode_type_${option}`}
                      value={option}
                      checked={values.move_mode_type === option}
                      onChange={(e) => {
                        handleChange(e)
                        setStatus({ ...status, move_mode_type_error: '' })
                      }}
                    />
                    <label className="form-check-label" htmlFor={`move_mode_type_${option}`}>
                      {t(`move_modes.move_mode_types.${option}`)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )
        }

        const onOrganizationsChange = (organizations) => {
          const updatedOrganizations = values.move_mode_organizations.map((org) => {
            const isSelected = organizations.find((o) => o.value === org.organization_id);

            if (!isSelected) {
              return { ...org, _destroy: true };
            }

            return org;
          });

          organizations.forEach((org) => {
            const exists = updatedOrganizations.find((o) => o.organization_id === org.value);
            if (!exists) {
              updatedOrganizations.push({ organization_id: org.value, _destroy: false });
            }
          });

          enhancedHandleChange({
            target: {
              name: 'move_mode_organizations',
              value: updatedOrganizations,
            },
          });
        };

        const renderOrganizationsSelect = (label, name) => {
          const selectOptions = organizationOptions.map(organization => ({
            label: organization.name, value: organization.id
          }))

          const selectedOrganizations = values.move_mode_organizations.filter((org) => !org._destroy).map(org => org.organization_id)
          const selectValues = selectOptions.filter(opt => selectedOrganizations.includes(opt.value))

          return (
            <div className="form-group">
              <label>{label}</label>
              <Select classNamePrefix='react-select'
                className={`react-select inverted ${touched[name] && errors[name] && 'is-invalid'}`}
                options={selectOptions}
                placeholder='Select organizations'
                id={name}
                name={name}
                onBlur={handleBlur}
                value={selectValues}
                onChange={onOrganizationsChange}
                isMulti
              />
              {errors[name] && touched[name] && <div className="invalid-feedback">{errors[name]}</div>}
            </div>
          )
        }

        const renderServoMoveStepSelect = (index: number) => {
          const options =
            values.move_mode_type === 'cooking_mode'
              ? cookingMoveSteps.map((step) => ({ label: step.name, value: step.id }))
              : unloadMoveSteps.map((step) => ({ label: step.name, value: step.id }));

          return (
            <div className="form-group">
              <label>Submove</label>
              <Select
                classNamePrefix="react-select"
                className={`react-select`}
                options={options}
                placeholder={t('move_mode_steps.select_submove')}
                id={`move_mode_steps[${index}].servo_move_step_id`}
                name={`move_mode_steps[${index}].servo_move_step_id`}
                value={
                  options.find(
                    (opt) => opt.value === values.move_mode_steps[index].servo_move_step_id
                  ) || null
                }
                onChange={(selectedOption) => {
                  const updatedMoveModeSteps = [...values.move_mode_steps];
                  updatedMoveModeSteps[index].servo_move_step_id = selectedOption?.value
                  setStatus({ ...status, move_mode_steps: updatedMoveModeSteps });
                }}
                onBlur={handleBlur}
              />
              {touched.move_mode_steps?.[index]?.servo_move_step_id &&
                errors.move_mode_steps?.[index] && (
                  <div className="invalid-feedback">
                    {errors.move_mode_steps[index].toString()}
                  </div>
              )}
            </div>
          );
        };

        const renderDurationTypeRadio = (index: number) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                height: '100%',
              }}
            >
              <label
                style={{
                  marginRight: '15px',
                  fontSize: '1rem',
                  marginBottom: 0,
                }}
              >
                {t('move_mode_steps.duration_type')}:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {['seconds', 'repetitions'].map((option) => (
                  <div
                    key={option}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="radio"
                      name={`move_mode_steps[${index}].duration_type`}
                      id={`move_mode_steps[${index}].duration_type_${option}`}
                      value={option}
                      style={{ verticalAlign: 'middle' }}
                      checked={values.move_mode_steps[index].duration_type === option}
                      onChange={(e) => {
                        handleChange(e);
                        setStatus({ ...status, [`move_mode_steps[${index}].duration_type_error`]: '' });
                      }}
                    />
                    <label
                      htmlFor={`move_mode_steps[${index}].duration_type_${option}`}
                      style={{
                        margin: 0,
                        fontSize: '1rem',
                        verticalAlign: 'middle',
                        color: 'black'
                      }}
                    >
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          );
        };

        const renderStepDuration = (index: number) => {
          return (
            <div
              className="form-group"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '20px',
              }}
            >
              <div style={{ flex: '1' }}>
                {renderField(
                  t('move_mode_steps.duration'),
                  `move_mode_steps[${index}].duration`,
                  'number',
                  t('move_mode_steps.duration'),
                  values.move_mode_steps[index].duration,
                  false
                )}
              </div>

              <div style={{ flex: '1' }}>
                {renderDurationTypeRadio(index)}
              </div>
            </div>
          )
        }

        return (
          <form onSubmit={handleSubmit} className="form-robis">
            {renderMoveModeTypeRadio()}
            {renderField(t('move_modes.name'), 'name', 'text', t('move_modes.name'))}
            {renderField(t('move_modes.description'), 'description', 'text', t('move_modes.description'))}
            {renderOrganizationsSelect(t('move_modes.organizations'), 'move_mode_organizations')}

            <FieldArray name="move_mode_steps">
              {({ remove, push }) => {
                const sortedMoveModeSteps = values.move_mode_steps
                  .filter((move) => !move._destroy)
                  .sort((a, b) => a.step_number - b.step_number);

                const nextStepNumber =
                  sortedMoveModeSteps.length > 0
                    ? Math.max(...sortedMoveModeSteps.map((step) => step.step_number)) + 1
                    : 1;

                const renumberSteps = (steps) => {
                  const reorderedSteps = steps
                    .filter((step) => !step._destroy)
                    .sort((a, b) => a.step_number - b.step_number)
                    .map((step, index) => ({
                      ...step,
                      step_number: index + 1,
                    }));

                  return reorderedSteps.concat(steps.filter((step) => step._destroy));
                };

                return (
                  <div>
                    <h5 style={{ margin: '40px 0 20px 10px' }}>{t('move_mode_steps.title')}</h5>
                    {sortedMoveModeSteps.map((_, index) => (
                      <div
                        key={index}
                        className="move-mode-step-fields"
                        style={{
                          backgroundColor: 'white',
                          padding: '20px',
                          borderRadius: '20px',
                          marginBottom: '20px',
                        }}
                      >
                        <h6 style={{ margin: '10px 0 20px 10px' }}>
                          {t('move_mode_steps.move_mode_step')} #{index + 1}
                        </h6>

                        {renderServoMoveStepSelect(index)}
                        {renderStepDuration(index)}

                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            const updatedMoveModeSteps = [...values.move_mode_steps];
                            updatedMoveModeSteps[index]._destroy = true;

                            const renumberedSteps = renumberSteps(updatedMoveModeSteps);

                            setFieldValue('move_mode_steps', renumberedSteps);
                          }}
                          disabled={sortedMoveModeSteps.length <= 1}
                        >
                          {t('move_mode_steps.remove')}
                        </button>
                      </div>
                    ))}
                    {sortedMoveModeSteps.length < 4 && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          push({
                            duration: 0,
                            duration_type: 'seconds' as DurationType,
                            servo_move_step_id: null,
                            step_number: nextStepNumber,
                            _destroy: false,
                          })
                        }
                      >
                        <i className="fa fa-plus" style={{ marginRight: '5px' }} /> {t('move_mode_steps.add')}
                      </button>
                    )}
                  </div>
                );
              }}
            </FieldArray>

            <div className="form-actions d-flex justify-content-end">
              <a className="btn btn-secondary inverted me-2" href="/admin/move_modes">
                {t('cancel')}
              </a>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {t('save')}
              </button>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

export default MoveModeForm
